import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FlyoutOpeningData } from '../flyout-models';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { FlyoutService } from '../flyout.service';
import { SnackBarPosition, SnackBarService } from '../../core/snack-bar.service';
import { AudienceService } from '../../audience/audience.service';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  Audience,
  AudienceFilter,
  AudienceFilterApp,
  AudienceFilterConfig,
  AudienceFilterTimeOperator,
  AudienceFilterType,
  ExampleAudienceConfig,
} from '../../audience/components/audience.model';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationsService } from '../../shared/services/integrations.service';
import AudienceHelpers from '../../audience/audience-helpers';
import { AndOrOperator, Widget, WidgetSimpleInfo } from '../../campaign/widgets.model';
import { WidgetService } from '../../shared/services/widget.service';
import { MatDialog } from '@angular/material/dialog';
import { FilterItemDialogComponent } from '../filter-item-dialog/filter-item-dialog.component';
import { CampaignStatus } from '../../campaign/campaign.model';
import { Subscription } from 'rxjs';
import { AudienceReachService } from '../../audience/audience-reach.service';
import { AccountService } from '../../account/account.service';
import { Account } from '../../back-office/account.model';
import { MatCheckbox } from '@angular/material/checkbox';
import { mergeMap } from 'rxjs/operators';
import {
  SessionCondition,
  SessionConditionConfig,
  SessionConditionConfigGroup,
  SessionConditionType,
} from '../../audience/components/session-condition-group.model';
import SessionConditionHelpers from '../../audience/session-condition-helpers';
import { SessionConditionDialogComponent } from '../session-condition-dialog/session-condition-dialog.component';
import { IMtr } from 'app/core/tracker/mtr.interface';
import { IAmplitude } from 'app/core/tracker/amplitude.service';
import { IntegrationType } from 'app/automations/integrations-models';
import { CurrentContextService } from 'app/core/current-context.service';
import { AccountUpgradePopupService } from 'app/account-upgrade/account-upgrade-popup/account-upgrade-popup.service';
import { ApplicationStarterService } from 'app/core/application-starter.service';

@Component({
  selector: 'app-flyout-audience',
  templateUrl: './flyout-audience.component.html',
  styleUrls: ['./flyout-audience.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutAudienceComponent implements OnInit, OnDestroy {
  @ViewChild('accountGroupCheckbox') private accountCheckbox: MatCheckbox;

  /** Gates the "Logged in" filter option (account has logged-in detection configured). */
  protected loggedInDetectionEnabled = false;

  private readonly logger: Logger;
  private _openingData: FlyoutOpeningData;
  public audienceIsExisting: boolean;
  public showSuggestions = false;

  public updateFormGroup: UntypedFormGroup;
  public audience: Audience;
  public exampleAudienceConfig: ExampleAudienceConfig;
  public audienceToCalculateReach: Audience;
  public AudienceFilterType = AudienceFilterType;

  public loadingWidgetsForAudience = false;
  public widgetsOnAudience: Array<Widget>;
  public allWidgets: Array<WidgetSimpleInfo>;

  public audienceFilters = [] as Array<AudienceFilter>;
  public conditions = [] as Array<SessionCondition>;
  public updateInProgress = false;
  public isRemovable = true;
  public deleteInProgress = false;

  public loadingAudienceReach = false;
  public loadingAudienceTotalReach = false;

  public audienceCampaignsArray: Array<{
    campaignId: number;
    name: string;
    imageUrl: string;
    status: CampaignStatus;
  }>;
  public numOfCampaigns = 0;
  public numOfCampaignsOnAllAccounts = 0;
  public showCampaignDetails = false;
  public CampaignStatuses = CampaignStatus;
  public ACTIVE_CMP_STATUSES = [CampaignStatus.Published, CampaignStatus.Scheduled];
  public INACTIVE_CMP_STATUSES = [CampaignStatus.Unpublished, CampaignStatus.Finished];
  public numOfPublishedCampaigns = undefined;
  public totalAudienceWeeklyReach: number;
  public reachPercentage: number;

  public allFilterConfigs = [] as Array<AudienceFilterConfig>;
  public supportedFilterConfigs = [] as Array<AudienceFilterConfig>;
  public allConditionsConfigGroups = [] as Array<SessionConditionConfigGroup>;
  public trimSubscription: Subscription;
  private currentContextSubscription: Subscription;
  private subscriptionArray: Subscription[] = [];

  public currentAudienceOperator: AndOrOperator;
  public currentAccount: Account;
  public availableOnAccountGroup = false;
  public activeApps: Array<string>;
  public AudienceOperators = AndOrOperator;
  public loadingWidgetsDataForAudience = true;
  public isDisconnectAudience = false;
  public AudienceFilterTypes = AudienceFilterType;
  public AudienceFilterTimeOperator = AudienceFilterTimeOperator;
  public isAdmin: boolean;

  @Input()
  public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this._openingData = openingData;
    this.setUpAudienceData();
  }

  public get flyoutOpeningData(): FlyoutOpeningData {
    return this._openingData;
  }

  // Native-app (and other restricted-surface) campaigns can't use client-side session conditions on audiences.
  public get disableSessionConditions(): boolean {
    return !!this._openingData?.sessionConditionsUnavailableKey;
  }

  constructor(
    protected readonly loggerFactory: LoggerFactory,
    private readonly flyoutService: FlyoutService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private readonly translateService: TranslateService,
    private readonly audienceReachService: AudienceReachService,
    private readonly integrationsService: IntegrationsService,
    private readonly widgetService: WidgetService,
    private readonly snackbarService: SnackBarService,
    private readonly audienceService: AudienceService,
    private readonly accountService: AccountService,
    private readonly currentContextService: CurrentContextService,
    @Inject('Mtr') private readonly mtr: IMtr,
    @Inject('Amplitude') private readonly amplitude: IAmplitude,
    private readonly accountUpgradePopupService: AccountUpgradePopupService,
  ) {
    this.logger = loggerFactory.get('FlyoutAudienceComponent');

    this.updateFormGroup = this.formBuilder.group({
      audienceName: ['', [Validators.required, Validators.maxLength(100), this.lengthValidator]],
    });
  }

  ngOnInit(): void {
    this.loadAllWidgets();
    this.initializeSupportedConditions();

    this.currentContextSubscription = this.currentContextService.currentContextBS.subscribe((currentContext) => {
      if (currentContext) {
        this.currentAccount = currentContext.account;
        this.isAdmin = currentContext.user.isAdmin;
        this.loggedInDetectionEnabled = currentContext.account?.loggedInDetection != null;
      }
      this.recalculateAudienceReach();
    });

    /*this.trimSubscription = this.updateFormGroup.valueChanges.pipe(debounceTime(1000)).subscribe((formValues) => {
      const trimmedValues = Object.keys(formValues).reduce((previous, currentKey) => ({...previous,
        [currentKey]: typeof formValues[currentKey] === 'string' ? formValues[currentKey].trim() : formValues[currentKey],
      }), {});
      this.updateFormGroup.patchValue(trimmedValues);
    });*/
  }

  private setUpAudienceData() {
    if (this._openingData) {
      this.isRemovable = this._openingData.isRemovable !== false;
      if (this._openingData.activeApps) {
        this.activeApps = this._openingData.activeApps;
        this.initializeSupportedFilters();
      } else {
        this.initializeActiveApps();
      }
      this.subscriptionArray.push(
        this.integrationsService.activeIntegrationsBS.subscribe(() => {
          this.applyCustomIntegrationOverrideToConfigs();
          this.audienceFilters?.forEach((f) => this.translateAudienceFilter(f));
        }),
      );
      if (!this.integrationsService.activeIntegrationsBS.getValue()) {
        this.integrationsService.loadActiveIntegrations();
      }
      if (this._openingData.widgets) {
        this.allWidgets = this._openingData.widgets;
      } else {
        this.loadAllWidgets();
      }
      if (this._openingData.audience) {
        this.audienceIsExisting = true;
        this.audience = this._openingData.audience;
        if (this.audience.isDefault === true) {
          this.audience.name = this.translateService.instant(this.audience.name);
        }

        this.updateFormGroup.get('audienceName').patchValue(this._openingData.audience.name);
        if (this.audience.isDefault === true) {
          this.updateFormGroup.get('audienceName').disable();
        }
        this.populateFiltersWithConfig();
        this.populateConditionsWithConfig();
        this.loadWidgetsForAudience();
        this.audienceFilters = this.audience.filters ? this.audience.filters : [];
        if (this.audienceFilters && this.audienceFilters.length > 0 && !this.audienceFilters[0].filterTranslations) {
          this.translateAudienceFilters();
        }
        this.availableOnAccountGroup = !!this.audience.accountGroupId;
        this.recalculateAudienceReach();
        this.conditions = this.audience.sessionConditions ? this.audience.sessionConditions : [];
        if (this.conditions && this.conditions.length > 0 && !this.conditions[0].conditionTranslations) {
          this.translateConditionFilters();
        }
        this.currentAudienceOperator = this.audience.operator;
      } else if (this._openingData.exampleAudienceConfig) {
        this.exampleAudienceConfig = this._openingData.exampleAudienceConfig;
        this.currentAudienceOperator = this._openingData.exampleAudienceConfig.operator;
        this.audienceIsExisting = false;
        this.updateFormGroup
          .get('audienceName')
          .patchValue(this.translateService.instant(this.exampleAudienceConfig.titleKey));
        this.updateFormGroup.get('audienceName').enable();
        const audienceFilters = this._openingData.exampleAudienceConfig.filters;
        if (audienceFilters) {
          audienceFilters.forEach((af) => {
            //af.frequencyValue needed to be deleted after testing new filters
            af.filterConfig = AudienceHelpers.getFilterConfig(af.type, af.app, af.frequencyValue);
            af = this.translateAudienceFilter(af, false);
            if (!af.value) {
              af.filterTranslations.value = this.translateService.instant(this.exampleAudienceConfig.chooseItemKey);
            }
            return af;
          });
        }
        this.audienceFilters = audienceFilters ? audienceFilters : [];

        const sessionConditions = this._openingData.exampleAudienceConfig.sessionConditions;
        if (sessionConditions) {
          sessionConditions.forEach((sc) => {
            sc.conditionConfig = SessionConditionHelpers.getSessionConditionConfig(sc.type);
            sc = this.translateSessionCondition(sc, false);
            if (!sc.value) {
              if (sc.chooseItemKey) {
                sc.conditionTranslations.value = this.translateService.instant(sc.chooseItemKey);
              }
            }
            return sc;
          });
        }
        this.conditions = sessionConditions ? sessionConditions : [];

        this.recalculateAudienceReach();
      } else {
        this.audienceIsExisting = false;
        this.updateFormGroup.get('audienceName').patchValue('');
        this.updateFormGroup.get('audienceName').enable();
        this.audienceFilters = [];
        this.conditions = [];
        this.currentAudienceOperator = AndOrOperator.And;
        this.recalculateAudienceReach();
      }
    }
  }

  private checkIfAudienceIsOnOtherAccounts() {
    if (!!this.audience.accountGroupId && this.numOfCampaigns === 0) {
      this.widgetService.getWidgetsByAudienceIdFromAllAccounts(this.audience.id).subscribe(
        (widgets: Array<Widget>) => {
          this.loadingWidgetsForAudience = false;
          this.numOfCampaignsOnAllAccounts = widgets ? widgets.length : 0;
        },
        () => {
          this.loadingWidgetsForAudience = false;
          this.snackbarService.error('generic.error');
        },
      );
    }
  }

  private initializeActiveApps() {
    this.integrationsService.getActiveApps().subscribe(
      (activeApps: string[]) => {
        this.activeApps = activeApps;
        this.initializeSupportedFilters();
      },
      () => {
        this.activeApps = undefined;
        this.initializeSupportedFilters();
      },
    );
  }

  private recalculateAudienceReach(): void {
    this.audienceToCalculateReach = {
      isDefault: this.audience && this.audience.isDefault === true,
      filters: this.audienceFilters,
      sessionConditions: this.audience?.sessionConditions,
      name: this.audience ? this.audience?.name : this.updateFormGroup.value.audienceName.trim(),
    } as Audience;
    this.audienceReachService.populateReachOnAudience(this.audienceToCalculateReach, undefined, true);

    // Calculating reach of all visitors
    if (AudienceHelpers.isInternalAudience(this.audienceFilters)) {
      this.audienceReachService.getTotalAudiencePotentialForLast7Days().subscribe(
        (result: any) => {
          this.totalAudienceWeeklyReach = result.data;
          this.calculateReachPercentage();
        },
        (error) => {
          console.error('Could not calculate audience reach: ', error);
          this.totalAudienceWeeklyReach = undefined;
          this.calculateReachPercentage();
        },
      );
    } else {
      this.totalAudienceWeeklyReach = undefined;
      this.calculateReachPercentage();
    }
  }

  private calculateReachPercentage() {
    if (this.totalAudienceWeeklyReach && this.audienceToCalculateReach && this.audienceToCalculateReach.reach) {
      this.reachPercentage = Math.round((this.audienceToCalculateReach.reach / this.totalAudienceWeeklyReach) * 100);
    } else {
      this.reachPercentage = undefined;
    }
  }

  private loadAllWidgets() {
    this.widgetService.getAllWidgetsSimpleInfo().subscribe(
      (widgets: Array<WidgetSimpleInfo>) => {
        this.allWidgets = widgets;
      },
      () => {
        this.snackbarService.error('generic.error');
      },
    );
  }

  private loadWidgetsForAudience() {
    this.loadingWidgetsForAudience = true;
    this.audienceCampaignsArray = [] as Array<{
      campaignId: number;
      name: string;
      imageUrl: string;
      status: CampaignStatus;
    }>;
    this.numOfCampaigns = undefined;
    this.widgetService.getWidgetsByAudienceId(this.audience.id).subscribe(
      (widgets: Array<Widget>) => {
        this.loadingWidgetsForAudience = false;
        this.widgetsOnAudience = widgets;
        if (!this.widgetsOnAudience || this.widgetsOnAudience.length === 0) {
          this.numOfCampaigns = 0;
          this.numOfPublishedCampaigns = 0;
        } else {
          let numCampaigns = 0;
          let numPublished = 0;
          this.widgetsOnAudience.forEach((widget: Widget) => {
            if (this.audienceCampaignsArray.findIndex((c) => c.campaignId === widget.campaign.id) === -1) {
              this.audienceCampaignsArray.push({
                campaignId: widget.campaign.id,
                name: widget.campaign.name,
                imageUrl:
                  widget.states.length > 0 && widget.states[0].previewImageUrl
                    ? widget.states[0].previewImageUrl
                    : undefined,
                status: widget.campaign.status,
              });
              numCampaigns++;
              numPublished += widget.campaign.status === CampaignStatus.Published ? 1 : 0;
            }
          });
          this.numOfCampaigns = numCampaigns;
          this.numOfPublishedCampaigns = numPublished;
        }
        this.loadingWidgetsDataForAudience = false;
        this.checkIfAudienceIsOnOtherAccounts();
      },
      () => {
        this.loadingWidgetsForAudience = false;
        this.snackbarService.error('generic.error');
      },
    );
  }

  private initializeSupportedConditions() {
    this.allConditionsConfigGroups = SessionConditionHelpers.getAudienceSessionConditionConfigGroups();
  }

  private initializeSupportedFilters() {
    this.allFilterConfigs = AudienceHelpers.getAllFilterConfigs();
    // Initialize filter configs without apps
    this.supportedFilterConfigs = this.allFilterConfigs.filter((f) => !f.app);
    this.supportedFilterConfigs = this.supportedFilterConfigs.filter((x) => x.hide !== true);

    if (this.activeApps && this.activeApps.length > 0) {
      const configuredAppConfigs = this.allFilterConfigs.filter((fc) => {
        if (fc.app && this.integrationsService.getAppName(fc.app) === AudienceFilterApp.Mailchimp) {
          return !!fc.app && this.activeApps.indexOf(IntegrationType.MailChimp) !== -1;
        } else {
          return !!fc.app && this.activeApps.indexOf(this.integrationsService.getAppName(fc.app)) !== -1;
        }
      });
      this.supportedFilterConfigs = [...this.supportedFilterConfigs, ...configuredAppConfigs];
    }

    this.applyCustomIntegrationOverrideToConfigs();

    this.accountService.getCurrentAccount().subscribe((account: Account) => {
      this.currentAccount = account;

      // Remove Region filter if ipDb not IPRegistry
      if (this.currentAccount.ipDb !== 'IPRegistry') {
        this.supportedFilterConfigs = this.supportedFilterConfigs.filter((x) => x.type !== AudienceFilterType.Region);
      }
    });
  }

  private applyCustomIntegrationOverrideToConfigs(): void {
    const customData = this.integrationsService.activeIntegrationsBS
      .getValue()
      ?.find((i) => i.type === IntegrationType.Custom)?.data;
    if (!customData?.name && !customData?.iconUrl) {
      return;
    }
    const segmentSuffix = this.translateService.instant('audience.type-titles.apps.custom.segment-suffix');
    const overrideCfg = (cfg: AudienceFilterConfig): AudienceFilterConfig => ({
      ...cfg,
      iconUrl: customData.iconUrl || cfg.iconUrl,
      titleOverride: customData.name ? `${customData.name} ${segmentSuffix}` : cfg.titleOverride,
    });
    this.supportedFilterConfigs = this.supportedFilterConfigs.map((cfg) =>
      cfg.app === AudienceFilterApp.Custom ? overrideCfg(cfg) : cfg,
    );
    // Also override the per-filter config used by the chip rendering.
    this.audienceFilters?.forEach((f) => {
      if (f.app === AudienceFilterApp.Custom && f.filterConfig) {
        f.filterConfig = overrideCfg(f.filterConfig);
      }
    });
  }

  public isDisabled(config: AudienceFilterConfig | SessionConditionConfig): boolean {
    if (config?.disabledOnTiers?.includes(this.currentAccount.tier)) {
      return true;
    }
    return false;
  }

  public addFilterToAudience(config: AudienceFilterConfig) {
    if (this.isDisabled(config)) {
      this.accountUpgradePopupService.openPopup('audience-' + config.type);
      return true;
    }

    const createAudienceFilterDialogRef = this.dialog.open(FilterItemDialogComponent, {
      panelClass: 'filter-item-dialog-component-container',
      autoFocus: false,
      minWidth:
        config.type === AudienceFilterType.Goals || config.type === AudienceFilterType.Purchase ? '540px' : '480px',
      width: config.type === AudienceFilterType.Goals || config.type === AudienceFilterType.Purchase ? 'auto' : '480px',
      data: {
        newFilterConfig: config,
        allWidgets: this.allWidgets,
        audienceFilters: this.audienceFilters,
      },
    });

    createAudienceFilterDialogRef
      .afterClosed()
      .subscribe((result: { audienceFilter: AudienceFilter; skipTranslatingValue?: boolean; listId }) => {
        if (result) {
          if (result.audienceFilter.type !== AudienceFilterType.AppTags) {
            result.audienceFilter = this.translateAudienceFilter(
              result.audienceFilter,
              result.skipTranslatingValue === true,
            );
          } else {
            if (result.audienceFilter.app === AudienceFilterApp.Custobar) {
              result.audienceFilter = this.translateAudienceFilter(
                {
                  ...result.audienceFilter,
                  value: JSON.stringify({
                    TagId: result.audienceFilter.value,
                  }),
                },
                result.skipTranslatingValue === true,
              );
            } else {
              result.audienceFilter = this.translateAudienceFilter(
                {
                  ...result.audienceFilter,
                  value: JSON.stringify({
                    TagId: result.audienceFilter.value,
                    ListId: result.listId,
                  }),
                },
                result.skipTranslatingValue === true,
              );
            }
          }
          this.audienceFilters.push(result.audienceFilter);
          this.recalculateAudienceReach();
        }
      });
    this.initializeSupportedFilters();
  }

  public addConditionToGroup(config: SessionConditionConfig) {
    if (this.isDisabled(config)) {
      this.accountUpgradePopupService.openPopup('session-condition-' + config.type);
      return;
    }

    if (config.defaultOperator) {
      const createSessionConditionDialogRef = this.dialog.open(SessionConditionDialogComponent, {
        panelClass: 'session-condition-dialog-component-container',
        disableClose: true,
        autoFocus: false,
        width: config.type === SessionConditionType.JsExpression ? '640px' : '480px',
        data: {
          newConditionConfig: config,
          allWidgets: this.allWidgets,
          accountDomain: this.currentAccount ? this.currentAccount.urls[0] : undefined,
        },
      });

      createSessionConditionDialogRef
        .afterClosed()
        .subscribe((result: { sessionCondition: SessionCondition; skipTranslatingValue?: boolean }) => {
          if (result) {
            result.sessionCondition = this.translateSessionCondition(
              result.sessionCondition,
              result.skipTranslatingValue === true,
            );
            this.conditions.push(result.sessionCondition);
          }
        });
    } else {
      let condition = SessionConditionHelpers.conditionConfigToCondition(config);
      condition = this.translateSessionCondition(condition, true);
      this.conditions.push(condition);
    }
  }

  public isLimitReached(filterConfig: AudienceFilterConfig): boolean {
    // Check if no filters exists (might be only when AccountSettings API isn't responding)
    if (!this.audienceFilters) {
      return false;
    }
    // Check if this is an app audience (only allow one app)
    if (filterConfig.app && this.audienceFilters.some((config) => config.app)) {
      return true;
    }

    // Check if this is an geoip audience (only allow one)
    if (this.isGeoIpType(filterConfig.type)) {
      return this.audienceFilters.some((config) => this.isGeoIpType(config.type));
    }

    // Check wether we should disable just my number of usage of type
    if (!filterConfig.limit) {
      return false;
    }
    const count: number = this.audienceFilters.filter(
      (config) => config.type === filterConfig.type || config.app === filterConfig.app,
    ).length;
    return count >= filterConfig.limit;
  }

  private isGeoIpType(type: AudienceFilterType) {
    return type === AudienceFilterType.Country || type === AudienceFilterType.Region;
  }

  private populateFiltersWithConfig(): void {
    if (this.audience.filters) {
      this.audience.filters.forEach((audienceFilter: AudienceFilter) => {
        //audienceFilter.frequencyValue needed to be deleted after testing new filters
        audienceFilter.filterConfig = AudienceHelpers.getFilterConfig(
          audienceFilter.type,
          audienceFilter.app,
          audienceFilter.frequencyValue,
        );
      });
    }
  }

  private populateConditionsWithConfig(): void {
    if (this.audience.sessionConditions) {
      this.audience.sessionConditions.forEach((condition: SessionCondition) => {
        condition.conditionConfig = SessionConditionHelpers.getSessionConditionConfig(condition.type);
      });
    }
  }

  public translateAudienceFilters(): void {
    if (!this.audienceFilters || !this.allWidgets) {
      return;
    }

    // AudienceHelpers.populateFilterValuesWithNamesFromApps(this.audienceFilters, this.integrationsService);

    if (this.audience.isDefault === true) {
      this.audience.name = this.translateService.instant(this.audience.name);
    }
    this.audienceFilters.forEach((audienceFilter: AudienceFilter) => {
      this.translateAudienceFilter(audienceFilter);
    });
    AudienceHelpers.populateAudiencesFilterValuesWithNamesFromApps([this.audience], this.integrationsService);
  }

  public translateConditionFilters(): void {
    if (!this.conditions) {
      return;
    }

    this.conditions.forEach((condition: SessionCondition) => {
      this.translateSessionCondition(condition);
    });
  }

  private translateAudienceFilter(audienceFilter: AudienceFilter, skipTranslatingValue = false): AudienceFilter {
    audienceFilter.filterTranslations = audienceFilter.filterTranslations ? audienceFilter.filterTranslations : {};
    const customIntegrationData = this.integrationsService.activeIntegrationsBS
      .getValue()
      ?.find((i) => i.type === IntegrationType.Custom)?.data;
    audienceFilter.filterTranslations.type = AudienceHelpers.getTranslationForAudienceFilterType(
      audienceFilter,
      this.translateService,
      customIntegrationData,
    );
    audienceFilter.filterTranslations.operator = AudienceHelpers.getShortTranslationForAudienceFilterOperator(
      audienceFilter,
      this.translateService,
    );
    if (skipTranslatingValue !== true) {
      audienceFilter.filterTranslations.value = AudienceHelpers.getTranslationForAudienceFilterValue(
        audienceFilter,
        this.translateService,
        this.allWidgets,
      );
    }
    audienceFilter.filterTranslations.timeframeOperator =
      AudienceHelpers.getTranslationForAudienceFilterTimeframeOperator(audienceFilter, this.translateService);
    audienceFilter.filterTranslations.timeframeValue = AudienceHelpers.getTranslationForAudienceFilterTimeframeValue(
      audienceFilter,
      this.translateService,
    );
    return audienceFilter;
  }

  private translateSessionCondition(condition: SessionCondition, skipTranslatingValue = false): SessionCondition {
    condition.conditionTranslations = condition.conditionTranslations ? condition.conditionTranslations : {};
    condition.conditionTranslations.type = SessionConditionHelpers.getTranslationForSessionConditionType(
      condition,
      this.translateService,
    );
    condition.conditionTranslations.operator = SessionConditionHelpers.getShortTranslationForSessionConditionOperator(
      condition,
      this.translateService,
    );
    if (skipTranslatingValue !== true) {
      condition.conditionTranslations.value = SessionConditionHelpers.getTranslationForSessionConditionValue(
        condition,
        this.translateService,
        this.allWidgets,
      );
    }
    return condition;
  }

  public removeAudienceFilter(index: number, event: MouseEvent) {
    event.stopPropagation();
    if (this.audience?.isDefault === true) {
      return;
    }
    this.audienceFilters.splice(index, 1);
    this.recalculateAudienceReach();
  }

  public openAudienceFilterDialog(audienceFilter: AudienceFilter, index: number) {
    if (this.audience?.isDefault === true) {
      return;
    }
    const editAudienceFilterDialogRef = this.dialog.open(FilterItemDialogComponent, {
      panelClass: 'filter-item-dialog-component-container',
      autoFocus: false,
      minWidth:
        audienceFilter.type === AudienceFilterType.Goals || audienceFilter.type === AudienceFilterType.Purchase
          ? '540px'
          : '480px',
      width:
        audienceFilter.type === AudienceFilterType.Goals || audienceFilter.type === AudienceFilterType.Purchase
          ? 'auto'
          : '480px',
      data: {
        audienceFilter: audienceFilter,
        allWidgets: this.allWidgets,
      },
    });

    editAudienceFilterDialogRef
      .afterClosed()
      .subscribe((result: { audienceFilter: AudienceFilter; skipTranslatingValue?: boolean; listId }) => {
        if (result) {
          if (result.audienceFilter.type !== AudienceFilterType.AppTags) {
            result.audienceFilter = this.translateAudienceFilter(
              result.audienceFilter,
              result.skipTranslatingValue === true,
            );
          } else {
            if (result.audienceFilter.app === AudienceFilterApp.Custobar) {
              result.audienceFilter = this.translateAudienceFilter(
                {
                  ...result.audienceFilter,
                  value: JSON.stringify({
                    TagId: result.audienceFilter.value,
                  }),
                },
                result.skipTranslatingValue === true,
              );
            } else {
              result.audienceFilter = this.translateAudienceFilter(
                {
                  ...result.audienceFilter,
                  value: JSON.stringify({
                    TagId: result.audienceFilter.value,
                    ListId: result.listId,
                  }),
                },
                result.skipTranslatingValue === true,
              );
            }
          }
          this.audienceFilters[index] = result.audienceFilter;
          this.recalculateAudienceReach();
        }
      });
    this.initializeSupportedFilters();
  }

  public openSessionConditionDialog(sessionCondition: SessionCondition, index: number) {
    if (!sessionCondition?.conditionConfig.defaultOperator) {
      return;
    }
    const editAudienceFilterDialogRef = this.dialog.open(SessionConditionDialogComponent, {
      panelClass: 'session-condition-dialog-component-container',
      disableClose: true,
      autoFocus: false,
      width: sessionCondition.type === SessionConditionType.JsExpression ? '640px' : '480px',
      data: {
        sessionCondition: sessionCondition,
        allWidgets: this.allWidgets,
        accountDomain: this.currentAccount ? this.currentAccount.urls[0] : undefined,
      },
    });

    editAudienceFilterDialogRef
      .afterClosed()
      .subscribe((result: { sessionCondition: SessionCondition; skipTranslatingValue?: boolean }) => {
        if (result) {
          result.sessionCondition = this.translateSessionCondition(
            result.sessionCondition,
            result.skipTranslatingValue === true,
          );
          this.conditions[index] = result.sessionCondition;
        }
      });
  }

  public removeSessionCondition(index: number, event: MouseEvent) {
    event.stopPropagation();
    this.conditions.splice(index, 1);
  }

  public deleteAudience() {
    this.deleteInProgress = true;
    this.audienceService.deleteAudience(this.audience.id).subscribe(
      () => {
        const deletedAudience = this.audience;
        ApplicationStarterService.triggerEvent('onTriggerbeeClientAudienceDeleted', {
          id: deletedAudience.id,
          siteId: deletedAudience.siteId,
          name: deletedAudience.name,
        });
        this.snackbarService.success('audience.messages.audience-removed');
        this.deleteInProgress = false;
        this.flyoutService.closeAudienceFlyout({
          audience: this.audience,
          isRemoved: true,
        });
      },
      (error) => {
        this.deleteInProgress = false;
        this.logger.error('Audience could not be removed: ' + JSON.stringify(error));
        this.snackbarService.error('audience.messages.remove-failed');
      },
    );
  }

  public saveAudience() {
    this.updateFormGroup
      .get('audienceName')
      .setValue(AudienceHelpers.sanitizeAudienceName(this.updateFormGroup.value.audienceName));
    if (!this.validateAudienceName()) {
      return;
    }
    if (!this.validateAudienceFilters(this.audienceFilters, this.conditions)) {
      return;
    }
    const audienceToSend = {} as Audience;

    audienceToSend.name = this.updateFormGroup.value.audienceName.trim();
    audienceToSend.filters = this.audienceFilters;
    audienceToSend.sessionConditions = this.conditions;
    audienceToSend.accountGroupId =
      this.accountCheckbox?.checked && this.currentAccount?.groupId ? this.currentAccount.groupId : undefined;

    // Set operator
    const oneSideIsEmpty = audienceToSend.filters.length === 0 || audienceToSend.sessionConditions.length === 0;
    if (this.currentAudienceOperator === AndOrOperator.Or && oneSideIsEmpty) {
      this.currentAudienceOperator = AndOrOperator.And;
    }
    audienceToSend.operator = this.currentAudienceOperator;

    if (this.audienceIsExisting === true) {
      this.updateInProgress = true;
      audienceToSend.id = this.audience.id;
      this.audienceService.updateAudience(audienceToSend).subscribe(
        (updatedAudience: Audience) => {
          ApplicationStarterService.triggerEvent('onTriggerbeeClientAudienceUpdated', {
            id: updatedAudience.id,
            siteId: updatedAudience.siteId,
            name: updatedAudience.name,
          });
          this.snackbarService.success('audience.messages.audience-updated');
          this.updateInProgress = false;
          this.audience.name = updatedAudience.name;
          this.audience.accountGroupId = updatedAudience.accountGroupId;
          this.audience.operator = updatedAudience.operator;
          this.audience.filters = updatedAudience.filters;
          this.flyoutService.closeAudienceFlyout({
            audience: this.audience,
            isRemoved: this.isDisconnectAudience,
            widgetId: this._openingData.widgetId,
          });
        },
        (error) => {
          this.logger.error('Audience could not be saved: ' + JSON.stringify(error));
          this.updateInProgress = false;
          this.snackbarService.error('audience.messages.update-failed');
        },
      );
    } else {
      this.updateInProgress = true;
      this.audienceService.createAudience(audienceToSend).subscribe(
        (createdAudience: Audience) => {
          ApplicationStarterService.triggerEvent('onTriggerbeeClientAudienceCreated', {
            id: createdAudience.id,
            siteId: createdAudience.siteId,
            name: createdAudience.name,
          });
          this.snackbarService.success('audience.messages.audience-created');
          this.updateInProgress = false;
          audienceToSend.id = createdAudience.id;
          audienceToSend.createdTime = createdAudience.createdTime ?? new Date().toISOString();
          audienceToSend.updatedTime = createdAudience.updatedTime ?? audienceToSend.createdTime;
          this.audience = audienceToSend;
          this.audienceIsExisting = true;
          this.populateFiltersWithConfig();
          this.flyoutService.closeAudienceFlyout({
            audience: audienceToSend,
            isRemoved: false,
            isCreated: true,
            widgetId: this._openingData.widgetId,
            source: this._openingData?.source,
          });
          this.mtr.goal('Created audience');
          this.amplitude.logEvent('Created audience');
        },
        (error) => {
          this.logger.error('Audience could not be saved: ' + JSON.stringify(error));
          this.updateInProgress = false;
          this.snackbarService.error('audience.messages.create-failed');
        },
      );
    }
  }

  private validateAudienceFilters(
    audienceFilters: Array<AudienceFilter>,
    conditions: Array<SessionCondition>,
  ): boolean {
    if ((!audienceFilters || audienceFilters.length === 0) && (!conditions || conditions.length === 0)) {
      this.snackbarService.warning('audience.validation.no-filter', undefined, 5000, SnackBarPosition.BottomCenter);
      return false;
    }
    const invalidFilterInputsIndexes = [] as Array<number>;
    if (audienceFilters) {
      for (let i = 0; i < audienceFilters.length; i++) {
        if (!audienceFilters[i].value || audienceFilters[i].value === '') {
          invalidFilterInputsIndexes.push(i);
        }
      }
    }
    if (invalidFilterInputsIndexes.length > 0) {
      this.snackbarService.warning(
        'audience.validation.filter-invalid',
        undefined,
        5000,
        SnackBarPosition.BottomCenter,
      );
      return false;
    }

    const invalidConditionInputsIndexes = [] as Array<number>;
    if (conditions) {
      for (let i = 0; i < conditions.length; i++) {
        if (
          conditions[i].conditionConfig.requiresPopup === true &&
          (!conditions[i].value || conditions[i].value === '')
        ) {
          invalidConditionInputsIndexes.push(i);
        }
      }
    }
    if (invalidConditionInputsIndexes.length > 0) {
      this.snackbarService.warning(
        'audience.validation.condition-invalid',
        undefined,
        5000,
        SnackBarPosition.BottomCenter,
      );
      return false;
    }
    return true;
  }

  private lengthValidator(control: UntypedFormControl): ValidationErrors {
    if (control.value.trim().length < 4) {
      return { minlengthError: 'length must be at least 4 characters' };
    }
    return null;
  }

  public validateAudienceName() {
    this.updateFormGroup.get('audienceName').markAsDirty();
    if (this.updateFormGroup.get('audienceName').invalid) {
      if (this.updateFormGroup.get('audienceName').hasError('required')) {
        this.snackbarService.warning('audience.validation.no-name', undefined, 5000, SnackBarPosition.BottomCenter);
        return false;
      }
      if (this.updateFormGroup.get('audienceName').hasError('minlengthError')) {
        this.snackbarService.warning(
          'audience.validation.name-minlength',
          undefined,
          5000,
          SnackBarPosition.BottomCenter,
        );
        return false;
      }
      if (this.updateFormGroup.get('audienceName').hasError('maxlength')) {
        this.snackbarService.warning(
          'audience.validation.name-maxlength',
          undefined,
          5000,
          SnackBarPosition.BottomCenter,
        );
        return false;
      }
    }
    return true;
  }

  public cancelFlyout() {
    this.flyoutService.closeAudienceFlyout(undefined);
  }

  public ngOnDestroy() {
    this.subscriptionArray.push(this.trimSubscription, this.currentContextSubscription);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  public disconnectCampaignFromAudience(campaignDetails: {
    campaignId: number;
    name: string;
    imageUrl: string;
    status: CampaignStatus;
  }) {
    if (this.ACTIVE_CMP_STATUSES.indexOf(campaignDetails.status) !== -1) {
      return;
    }
    const widgetsToDisconnect = this.allWidgets.filter((w) => w.campaignId === campaignDetails.campaignId);
    for (let i = 0; i < widgetsToDisconnect.length; i++) {
      setTimeout(
        () => {
          this.widgetService
            .getWidgetById(widgetsToDisconnect[i].id)
            .pipe(
              mergeMap((widget: Widget) => {
                widget.audienceId = undefined;
                return this.widgetService.updateWidget(widget.id, widget);
              }),
            )
            .subscribe(
              () => {
                const campaignIndex = this.audienceCampaignsArray.findIndex(
                  (c) => c.campaignId === campaignDetails.campaignId,
                );
                if (campaignIndex !== -1) {
                  this.audienceCampaignsArray.splice(campaignIndex, 1);
                }
                this.numOfCampaigns = this.audienceCampaignsArray.length;
                this.numOfPublishedCampaigns = this.audienceCampaignsArray.filter(
                  (c) => this.ACTIVE_CMP_STATUSES.indexOf(c.status) !== -1,
                ).length;
                this.isDisconnectAudience = true;
                this.snackbarService.success('audience.statistics.campaign-disconnected');
              },
              () => {
                this.snackbarService.error('generic.error');
              },
            );
        },
        i * 500 + 1,
      );
    }
  }

  public openCampaignOverviewInNewTab(campaignId: number) {
    window.open('/campaigns/' + campaignId + '/settings', '_new');
  }
}
